/**
 * SceneTreeV2Container — feature-flagged wrapper for the virtualised Scene Tree.
 *
 * Bridges the existing UnifiedSceneTreeNode world into the new
 * SceneHierarchyManifest / SceneTreeV2 world per WP-A spec.
 *
 * Responsibilities:
 *   - Memoises the Tier-1 manifest conversion (buildTier1ManifestFromUnifiedTree).
 *   - Reads featureFlagEnabled from useSceneHierarchyStore.
 *   - When flag OFF → renders null (caller shows legacy SceneTreePanel instead).
 *   - When flag ON  → renders SceneTreeV2, translating node selections into
 *     SceneSelection via selectByIndexNodeId (source: "scene-tree").
 *   - Bidirectional sync: keeps useSceneHierarchyStore.selectedNodeId in sync
 *     with the incoming selectedNodeId prop.
 *
 * Phase 6.5 / INIT-2026-Q3-SCENE-HIERARCHY WP-A.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { tokens } from "@/design";
import type { UnifiedSceneTreeNode } from "@/app/scenePackage";
import { getToolCardKindFor } from "@/app/sceneExplorer";
import type { SceneSelection } from "@/app/sceneSelection";
import { selectByIndexNodeId } from "@/app/sceneSelection";
import { useSceneHierarchyStore } from "@/state/sceneHierarchyStore";
import { useViewerStore } from "@/state/useViewerStore";
import type { SceneItem } from "@/sdk-stub/gtwin-sdk/types";
import type {
  ObjectIndex,
  SceneHierarchyManifest,
  TreeNode,
  UniversalSceneNode,
} from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";
import { SceneTreeV2 } from "./SceneTreeV2";
import { RelationshipChips } from "./RelationshipChips";
import {
  clearIsolation,
  colorByAspect,
  isolate,
  isolateBlock,
  isolateSystem,
  setOpacity,
  toggleVisibility,
} from "./overlayController";
import { buildTier1ManifestFromUnifiedTree } from "./buildTier1Manifest";
import { objectIndexToManifest } from "./objectIndexToManifest";
import { computeTemporalStates } from "./temporalState";
import {
  buildPickContextFromNodes,
  resolveGeometryPick,
  type PickContext,
} from "./nodePickResolver";
import type { LayerHierarchyRef } from "./collectSceneHierarchies";
import { fetchObjectIndexManifest } from "./fetchObjectIndexManifest";
import {
  fetchObjectIndexSegment,
  resolveSegmentUrl,
} from "./fetchObjectIndexSegment";
import { LayerProvenancePanel } from "./LayerProvenancePanel";
import { isCatalogLayerVisibilityNode } from "./layerVisibilityNode";
import { appendAttachedLayerNodes } from "./attachedLayerNodes";

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

export interface SceneTreeV2ContainerProps {
  readonly tree: UnifiedSceneTreeNode | undefined;
  readonly gtwinId: string;
  /** Currently selected node id from the outer viewer state (controlled). */
  readonly selectedNodeId: string | null;
  /** Called when the user selects a node; receives a full SceneSelection. */
  readonly onSelect: (selection: SceneSelection) => void;
  /**
   * INIT-2026-Q3-SCENE-HIERARCHY Block 3 — real load path.
   * Object Index extracted from `payload.geospatial.objectIndex` in the
   * loaded gTwin record. When present, the manifest is built from this
   * index instead of the unified tree, and SceneTreeV2 renders regardless
   * of the feature flag (an objectIndex project is inherently a V2 project).
   * Optional — absent for normal gTwins; no behaviour change when absent.
   */
  readonly objectIndex?: ObjectIndex | null;
  /**
   * INIT-2026-Q3-SCENE-HIERARCHY close-out C2 — hierarchy-by-URL.
   * A `SceneItem.hierarchyUrl` discovered on the gTwin payload. When present
   * and no inline `objectIndex` is supplied, the container fetches the
   * `object-index.json` from this URL and renders it. This is the large-model
   * delivery path (G1): the index lives as an artifact, not inline.
   * Optional — absent for inline/legacy gTwins; no behaviour change when absent.
   */
  readonly hierarchyUrl?: string | null;
  /**
   * INIT-2026-Q3-SCENE-HIERARCHY multi-layer V1 — per-layer hierarchy refs.
   * When present, each entry triggers an independent manifest fetch; results
   * are merged into a grouped tree (layer → block → object).
   */
  readonly sceneHierarchies?: LayerHierarchyRef[];
  /**
   * INIT-2026-Q3-SCENE-HIERARCHY geometry-binding phase — NOTIFICATION.
   * Called BY THE CONTAINER after it successfully resolves a geometry pick
   * (engine hit → node found). Lets TwinShell update its own state (e.g.
   * highlight the mesh in Giro3D). No-op path: if the hit resolves to no
   * node, this callback is not called.
   */
  readonly onGeometryPick?: (hitName: string) => void;
  /**
   * INIT-2026-Q3-SCENE-HIERARCHY geometry-binding phase.
   * Called (and re-called on objectIndex change) with the current PickContext
   * AND a stable `triggerPick` function that TwinShell can call from the
   * Giro3D pick event to push a hit into the container.
   *
   * TwinShell stores the returned `triggerPick` in a ref and calls it from
   * the Giro3D adapter's `objectPick` event:
   *   `adapter.on('objectPick', (hit) => triggerPickRef.current?.(hit.name))`
   *
   * TwinShell also stores `ctx` to call `resolveTreeNodeToGeometry(node)` →
   * glTF name → ask the engine to highlight the corresponding mesh.
   */
  readonly onPickContextReady?: (
    ctx: PickContext,
    triggerPick: (hitName: string) => void,
  ) => void;
  readonly onBlockVisibilityChange?: (
    layerId: string,
    blockCode: string,
    visible: boolean,
  ) => void;
  readonly onBlockOpacityChange?: (
    layerId: string,
    blockCode: string,
    opacity: number,
  ) => void;
  /**
   * Layer-level visibility change. Called when a node that maps to a whole
   * rendered layer (e.g. a COG terrain / raster) is toggled — as opposed to a
   * block within a block-tiled tileset. Wired in TwinShell to
   * `host.setLayerVisible(layerId, visible)` so the engine actually hides the
   * layer (not just dims the tree row via the control overlay).
   */
  readonly onLayerVisibilityChange?: (
    layerId: string,
    visible: boolean,
  ) => void;
  /**
   * Layer-level opacity change. Called when a node mapping to a whole rendered
   * layer (e.g. a COG terrain / raster) has its opacity slider moved. Wired in
   * TwinShell to `host.setLayerOpacity(layerId, value)`.
   */
  readonly onLayerOpacityChange?: (
    layerId: string,
    opacity: number,
  ) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function SceneTreeV2Container({
  tree,
  gtwinId,
  selectedNodeId,
  onSelect,
  objectIndex,
  hierarchyUrl,
  sceneHierarchies = [],
  onGeometryPick,
  onPickContextReady,
  onBlockVisibilityChange,
  onBlockOpacityChange,
  onLayerVisibilityChange,
  onLayerOpacityChange,
}: SceneTreeV2ContainerProps) {
  const featureFlagEnabled = useSceneHierarchyStore(
    (s) => s.featureFlagEnabled,
  );
  // Dev/QA override: when a benchmark manifest is loaded (e.g. SHB via
  // window.__sceneHierarchy.loadBenchmark()), it takes precedence over the
  // manifest built from the live tree. Null in normal operation.
  const benchmarkManifest = useSceneHierarchyStore((s) => s.benchmarkManifest);

  // Large-model delivery (C2): manifest fetched from a SceneItem.hierarchyUrl.
  const urlObjectIndexManifest = useSceneHierarchyStore(
    (s) => s.urlObjectIndexManifest,
  );
  const loadObjectIndexFromUrl = useSceneHierarchyStore(
    (s) => s.loadObjectIndexFromUrl,
  );

  // When the gTwin delivers its hierarchy by reference (hierarchyUrl) and no
  // inline objectIndex is present, fetch + convert it once. Idempotent per URL.
  useEffect(() => {
    if (!objectIndex && hierarchyUrl) {
      void loadObjectIndexFromUrl(hierarchyUrl);
    }
  }, [objectIndex, hierarchyUrl, loadObjectIndexFromUrl]);

  // Multi-layer V1: load N per-layer hierarchy manifests independently.
  // Each result is stored in a map keyed by layerId and merged below.
  const [layerManifests, setLayerManifests] = useState<
    Map<string, { ref: LayerHierarchyRef; manifest: import("@/sdk-stub/gtwin-sdk/scene-hierarchy-types").SceneHierarchyManifest }>
  >(new Map());

  useEffect(() => {
    if (objectIndex || sceneHierarchies.length === 0) return;
    let cancelled = false;
    async function loadAll() {
      const results = await Promise.all(
        sceneHierarchies.map(async (ref) => {
          const manifest = await fetchObjectIndexManifest(ref.hierarchyUrl);
          return manifest ? { ref, manifest } : null;
        }),
      );
      if (cancelled) return;
      const next = new Map<string, { ref: LayerHierarchyRef; manifest: import("@/sdk-stub/gtwin-sdk/scene-hierarchy-types").SceneHierarchyManifest }>();
      for (const r of results) {
        if (r) next.set(r.ref.layerId, r);
      }
      setLayerManifests(next);
    }
    void loadAll();
    return () => { cancelled = true; };
  }, [objectIndex, sceneHierarchies]);

  // Build a merged multi-layer manifest when layer manifests are available.
  // Structure: synthetic layer-group node → (block-group if blockTiled) → objects.
  const multiLayerManifest = useMemo(() => {
    if (layerManifests.size === 0) return null;
    const allNodes: TreeNode[] = [];
    for (const [, { ref, manifest }] of layerManifests) {
      const layerGroupId = `layer-group:${ref.layerId}`;
      allNodes.push({
        id: layerGroupId,
        objectId: layerGroupId,
        parentId: null,
        lor: 2,
        label: ref.displayName,
        kind: "group",
        layerId: ref.layerId,
        featureId: null,
        domain: "smartcity",
        standardRef: null,
        metadata: { layerKind: "3dtiles", isLayerGroup: true },
        hasChildren: true,
        sortKey: 0,
        aspects: { spatial: { parentId: null } },
      } as TreeNode);

      if (ref.blockTiled) {
        const blockGroups = new Map<string, string>();
        // Identify manifest root nodes (converter-emitted asset/scene containers)
        // that the synthetic block group replaces. These must NOT appear in the
        // merged tree — otherwise they show up as stray siblings of the buildings.
        //
        // CRITICAL: a droppable root is a CONTAINER, i.e. a node that is actually
        // referenced as a parent by at least one other node. The merged
        // theme-level object-index emits every BUILDING as kind="asset" with
        // parentId=null (leaves), so filtering purely on (parentId==null && asset)
        // would drop every building and leave the layer empty. Requiring the node
        // to be referenced as a parent keeps leaf buildings while still removing
        // genuine per-block scene/asset roots.
        const referencedParentIds = new Set<string>();
        for (const n of manifest.nodes) {
          const direct = n.parentId ?? undefined;
          const spatial = n.aspects?.spatial?.parentId ?? undefined;
          if (direct) referencedParentIds.add(direct);
          if (spatial) referencedParentIds.add(spatial);
        }
        const manifestRootIds = new Set(
          manifest.nodes
            .filter(
              (n) =>
                n.parentId == null &&
                ((n.kind as string) === "asset" || (n.kind as string) === "scene") &&
                referencedParentIds.has(n.id),
            )
            .map((n) => n.id),
        );
        for (const node of manifest.nodes) {
          if (manifestRootIds.has(node.id)) continue;

          const blockCode =
            (node.metadata as Record<string, unknown> | undefined)?.blockCode as string | undefined;
          if (blockCode && !blockGroups.has(blockCode)) {
            const blockGroupId = `blk:${ref.layerId}:${blockCode}`;
            blockGroups.set(blockCode, blockGroupId);
            allNodes.push({
              id: blockGroupId,
              objectId: blockGroupId,
              parentId: layerGroupId,
              lor: 3,
              label: blockCode,
              kind: "group",
              layerId: ref.layerId,
              featureId: null,
              domain: "smartcity",
              standardRef: null,
              metadata: { blockCode, isBlockGroup: true },
              hasChildren: true,
              sortKey: 0,
              aspects: { spatial: { parentId: layerGroupId } },
            } as TreeNode);
          }
          const parentId = blockCode
            ? blockGroups.get(blockCode)!
            : layerGroupId;
          allNodes.push({
            ...node,
            parentId,
            layerId: node.layerId ?? ref.layerId,
            aspects: {
              ...node.aspects,
              spatial: {
                ...(node.aspects?.spatial ?? {}),
                parentId,
              },
            },
          } as TreeNode);
        }
      } else {
        for (const node of manifest.nodes) {
          const parentId = node.parentId ?? layerGroupId;
          allNodes.push({
            ...node,
            parentId,
            layerId: node.layerId ?? ref.layerId,
            aspects: {
              ...node.aspects,
              spatial: {
                ...(node.aspects?.spatial ?? {}),
                parentId,
              },
            },
          } as TreeNode);
        }
      }
    }
    return {
      schema: "geosoft.scene-hierarchy/1" as const,
      gtwinId,
      rootLayerId: "multi-layer",
      generatedAt: new Date().toISOString(),
      domain: "smartcity" as const,
      standardName: "CityGML",
      standardVersion: "3.0",
      nodes: allNodes,
      totalNodes: allNodes.length,
      nodeCountByLoR: {},
      paginated: false,
      dataSources: {},
    } satisfies SceneHierarchyManifest;
  }, [layerManifests, gtwinId]);

  // Memoised Tier-1 manifest with precedence order (Block 3 + C2 + multi-layer):
  //   1. benchmarkManifest        — dev/QA override (SHB console)
  //   2. objectIndex              — inline Object Index embedded in the gTwin
  //   3. multiLayerManifest       — merged N-layer grouped tree (V1)
  //   4. urlObjectIndexManifest   — single-URL index from SceneItem.hierarchyUrl
  //   5. buildTier1Manifest       — existing default (built from unified tree)
  const tier1Manifest = useMemo(
    () =>
      benchmarkManifest ??
      (objectIndex ? objectIndexToManifest(objectIndex) : null) ??
      multiLayerManifest ??
      urlObjectIndexManifest ??
      buildTier1ManifestFromUnifiedTree(tree ?? null, gtwinId),
    [benchmarkManifest, objectIndex, multiLayerManifest, urlObjectIndexManifest, tree, gtwinId],
  );

  // Tier-2 nodes keyed by the asset node id that triggered their load.
  // Populated on successful lazy-load; combined with Tier-1 nodes below.
  const [tier2NodesByParent, setTier2NodesByParent] = useState<
    Map<string, TreeNode[]>
  >(new Map());
  const catalogLayers = useViewerStore((s) => s.catalog);
  const slotMap = useViewerStore((s) => s.slotMap);

  // LRU eviction tracking: ordered list of parent ids by last-expanded time.
  // Head = oldest, tail = most recently expanded. (Task 2)
  const lruOrderRef = useRef<string[]>([]);
  const TIER2_LRU_BUDGET = 5000;

  const evictOldestIfOverBudget = useCallback(
    (current: Map<string, TreeNode[]>): Map<string, TreeNode[]> => {
      let total = 0;
      for (const nodes of current.values()) total += nodes.length;
      if (total <= TIER2_LRU_BUDGET) return current;

      const next = new Map(current);
      const lru = [...lruOrderRef.current];
      while (total > TIER2_LRU_BUDGET && lru.length > 0) {
        const oldest = lru.shift()!;
        const evicted = next.get(oldest);
        if (evicted) {
          total -= evicted.length;
          next.delete(oldest);
        }
      }
      lruOrderRef.current = lru;
      return next;
    },
    [],
  );

  /** Called by SceneTreeV2 when a node is collapsed — evicts its Tier-2 children. */
  const handleCollapse = useCallback(
    (nodeId: string) => {
      setTier2NodesByParent((prev) => {
        if (!prev.has(nodeId)) return prev;
        const next = new Map(prev);
        next.delete(nodeId);
        lruOrderRef.current = lruOrderRef.current.filter((id) => id !== nodeId);
        return next;
      });
    },
    [],
  );

  // Combined manifest: Tier-1 nodes merged with any loaded Tier-2 sub-trees.
  // Tier-2 root nodes (parentId === null in their original manifest) are
  // re-parented to the asset node id so treeProjection nests them correctly.
  // Deduped by id to tolerate double-loads.
  const manifest = useMemo(() => {
    if (tier2NodesByParent.size === 0) {
      return appendAttachedLayerNodes(tier1Manifest, catalogLayers, slotMap);
    }

    const allTier2: TreeNode[] = [];
    for (const nodes of tier2NodesByParent.values()) {
      for (const n of nodes) allTier2.push(n);
    }

    const seen = new Set<string>(tier1Manifest.nodes.map((n) => n.id));
    const newNodes = allTier2.filter((n) => !seen.has(n.id));

    const combined = {
      ...tier1Manifest,
      nodes: [...tier1Manifest.nodes, ...newNodes],
      totalNodes: tier1Manifest.totalNodes + newNodes.length,
    };
    return appendAttachedLayerNodes(combined, catalogLayers, slotMap);
  }, [tier1Manifest, tier2NodesByParent, catalogLayers, slotMap]);

  // C6/G6 — 4D timeline → ControlOverlay.temporalState. Nodes that carry a
  // temporalBinding (e.g. plant valve-state / pump-speed channels) get a
  // temporal state recomputed whenever the global timeline scrubs. Precompute
  // the bound-node subset so scrubbing a 34k-node model stays cheap.
  const temporalNodes = useMemo(
    () => manifest.nodes.filter((n) => (n.temporalBindings?.length ?? 0) > 0),
    [manifest],
  );
  const timelineTime = useViewerStore((s) => s.time);
  const setTemporalStates = useSceneHierarchyStore((s) => s.setTemporalStates);
  useEffect(() => {
    if (temporalNodes.length === 0) return;
    const timeMs = timelineTime ? timelineTime.getTime() : Date.now();
    setTemporalStates(computeTemporalStates(temporalNodes, timeMs));
  }, [temporalNodes, timelineTime, setTemporalStates]);

  // Memoised id → TreeNode lookup, built from the combined manifest so Tier-2
  // selections also resolve to full TreeNode metadata.
  const nodeById = useMemo(() => {
    const map = new Map<string, TreeNode>();
    for (const node of manifest.nodes) map.set(node.id, node);
    return map;
  }, [manifest]);

  // Block 2 — per-object control overlay (visibility / isolation / highlight /
  // colour). Subscribed here so the tree re-renders when any action writes it.
  const overlayByObject = useSceneHierarchyStore(
    (s) => s.controlOverlay.byObject,
  );
  const visibleLayerIds = useViewerStore((s) => s.visibleLayerIds);

  // Geometry-binding pick context — keyed by featureId (_BATCHID) AND
  // standardRef.nativeId. Built from the COMBINED manifest nodes so it works
  // whether the hierarchy was delivered inline (objectIndex) or by reference
  // (hierarchyUrl → urlObjectIndexManifest). Building from `objectIndex` alone
  // left by-URL hierarchies (CityGML) with an empty context, breaking the
  // tree↔3D link entirely.
  const pickContext = useMemo(
    () => buildPickContextFromNodes(manifest.nodes),
    [manifest],
  );

  // Resolve a geometry pick (glTF node name) to a TreeNode + SceneSelection,
  // then dispatch to the existing onSelect handler and update the ControlOverlay.
  // Also calls onGeometryPick (notification) on successful resolution.
  const onGeometryPickRef = useRef(onGeometryPick);
  onGeometryPickRef.current = onGeometryPick;
  const handleGeometryPick = useCallback(
    (hitName: string, hitFeatureId?: string | null) => {
      const resolved = resolveGeometryPick(hitName, hitFeatureId ?? null, pickContext);
      if (!resolved) return;
      onSelect(resolved.selection);
      useSceneHierarchyStore
        .getState()
        .setControlOverlayEntry(resolved.node.objectId, { highlighted: true });
      onGeometryPickRef.current?.(hitName);
    },
    [pickContext, onSelect],
  );

  // Expose the PickContext AND the stable triggerPick handle to the parent
  // (TwinShell) whenever the context changes. TwinShell stores triggerPick
  // and calls it from the Giro3D adapter's objectPick event.
  const onPickContextReadyRef = useRef(onPickContextReady);
  onPickContextReadyRef.current = onPickContextReady;
  useEffect(() => {
    onPickContextReadyRef.current?.(pickContext, handleGeometryPick);
  }, [pickContext, handleGeometryPick]);

  // Bidirectional sync: keep the V2 store in sync with the prop so any
  // listener of useSceneHierarchyStore sees the current selection.
  useEffect(() => {
    useSceneHierarchyStore.setState({ selectedNodeId });
  }, [selectedNodeId]);

  // Translate a raw node id into the full SceneSelection contract and
  // forward it to the outer onSelect handler.
  const handleSelect = useCallback(
    (nodeId: string | null) => {
      if (nodeId === null) return;
      const node = nodeById.get(nodeId);
      const catalogLayer = node?.layerId
        ? useViewerStore.getState().catalog.find((l) => l.id === node.layerId)
        : undefined;
      const metadataLayerKind =
        node?.metadata && typeof node.metadata.layerKind === "string"
          ? node.metadata.layerKind
          : undefined;
      const layerKind = catalogLayer
        ? getToolCardKindFor(catalogLayer) === "terrain"
          ? "terrain"
          : catalogLayer.kind
        : metadataLayerKind;
      onSelect(
        selectByIndexNodeId(nodeId, {
          source: "scene-tree",
          layerId: node?.layerId ?? undefined,
          layerKind,
          sceneItemId: node?.layerId ?? undefined,
          featureId:
            node?.featureId !== null && node?.featureId !== undefined
              ? node.featureId
              : undefined,
          nodeKind: node?.kind,
        }),
      );
    },
    [nodeById, onSelect],
  );

  // ── Block 2, Task B — relationship-chip navigation ─────────────────────────
  // Selecting a relationship target updates the V2 store selection AND emits a
  // SceneSelection through the existing onSelect pipeline so the tree scrolls
  // to the node and the camera/engine navigates to it.
  const handleNavigateToTarget = useCallback(
    (targetObjectId: string, target: UniversalSceneNode | null) => {
      useSceneHierarchyStore.getState().selectNode(targetObjectId);
      const resolved = target ?? nodeById.get(targetObjectId) ?? null;
      onSelect(
        selectByIndexNodeId(targetObjectId, {
          source: "scene-tree",
          layerId: resolved?.layerId ?? undefined,
          sceneItemId: resolved?.layerId ?? undefined,
          featureId:
            resolved?.featureId !== null && resolved?.featureId !== undefined
              ? resolved.featureId
              : undefined,
          nodeKind: resolved?.kind,
        }),
      );
    },
    [nodeById, onSelect],
  );

  // The currently-selected node (for the relationship-chip detail strip).
  const selectedNode = selectedNodeId ? nodeById.get(selectedNodeId) ?? null : null;

  // ── Lazy Tier-2 loader ────────────────────────────────────────────────────

  /**
   * Provided to SceneTreeV2 as `onLazyLoad`. Builds a minimal SceneItem from
   * the tree node, calls loadTier2For, and merges the result into the combined
   * manifest via `tier2NodesByParent` state.
   */
  const handleLazyLoad = useCallback(
    async (node: TreeNode): Promise<"not-implemented" | void> => {
      // Paginated object-index profile: a boundary node carries a static child
      // segment URL in metadata.hierarchyUrl (ending in `.json`). Fetch + merge
      // it directly — no domain adapter needed. The segment's objects are this
      // node's descendants, already linked by parentId (no re-parenting).
      const segUrl =
        typeof node.metadata?.hierarchyUrl === "string"
          ? (node.metadata.hierarchyUrl as string)
          : null;
      if (segUrl && segUrl.toLowerCase().endsWith(".json")) {
        // `manifest` is a union (object-index manifest | unified-tree manifest);
        // only the former carries segmentBaseUrl.
        const segmentBaseUrl =
          "segmentBaseUrl" in manifest
            ? (manifest as SceneHierarchyManifest).segmentBaseUrl
            : undefined;
        const resolved = resolveSegmentUrl(segUrl, segmentBaseUrl);
        const segNodes = await fetchObjectIndexSegment(resolved);
        if (segNodes && segNodes.length > 0) {
          lruOrderRef.current = [
            ...lruOrderRef.current.filter((id) => id !== node.id),
            node.id,
          ];
          setTier2NodesByParent((prev) =>
            evictOldestIfOverBudget(new Map(prev).set(node.id, segNodes)),
          );
          return;
        }
        // Empty/failed segment — surface as a genuine failure (Retry row).
        throw { kind: "fetch-failed" as const, cause: `segment ${resolved}` };
      }

      // layerKind is stored in metadata by buildTier1Manifest's convertNode.
      const layerKind =
        typeof node.metadata?.layerKind === "string"
          ? (node.metadata.layerKind as string)
          : "bim";

      const endpoints: Record<string, string> =
        typeof node.metadata?.href === "string"
          ? { href: node.metadata.href as string }
          : {};

      const item: SceneItem = {
        id: node.layerId ?? node.id,
        layerKind,
        endpoints,
        style: {},
      };

      const asset = {
        layerId: node.layerId ?? node.id,
        assetNodeId: node.id,
        gtwinId,
      };

      const result =
        await useSceneHierarchyStore.getState().loadTier2For(item, asset);

      if (result.ok) {
        // Re-parent Tier-2 root nodes (parentId === null) to the asset node id
        // so treeProjection nests them correctly under the Tier-1 asset.
        const tier2Nodes = result.manifest.nodes.map((n) =>
          n.parentId === null ? { ...n, parentId: node.id } : n,
        );
        lruOrderRef.current = [
          ...lruOrderRef.current.filter((id) => id !== node.id),
          node.id,
        ];
        setTier2NodesByParent((prev) =>
          evictOldestIfOverBudget(new Map(prev).set(node.id, tier2Nodes)),
        );
        return;
      }

      if (result.error.kind === "not-implemented") {
        return "not-implemented" as const;
      }

      // Genuine failure — throw so SceneTreeV2 shows the error row + Retry.
      throw result.error;
    },
    [gtwinId, evictOldestIfOverBudget, manifest],
  );

  // Show all — clears all visibility hides.
  const handleShowAll = useCallback(() => clearIsolation(), []);

  // Zoom to — emits a custom event that TwinShell can listen to. We resolve the
  // node's layerId here (nodeById is keyed by node.id) and include it in the
  // detail so the TwinShell listener can fly directly to the layer without
  // re-deriving it from the pick context (which is keyed by objectId).
  const handleZoomTo = useCallback(
    (nodeId: string) => {
      const node = nodeById.get(nodeId);
      const layerId = (node as { layerId?: string } | undefined)?.layerId ?? null;
      window.dispatchEvent(
        new CustomEvent("scene-tree:zoom-to", { detail: { nodeId, layerId } }),
      );
    },
    [nodeById],
  );

  const handleOpenSettings = useCallback(
    (nodeId: string) => {
      const node = nodeById.get(nodeId);
      const catalogLayer = node?.layerId
        ? useViewerStore.getState().catalog.find((l) => l.id === node.layerId)
        : undefined;
      const metadataLayerKind =
        node?.metadata && typeof node.metadata.layerKind === "string"
          ? node.metadata.layerKind
          : undefined;
      const layerKind = catalogLayer
        ? getToolCardKindFor(catalogLayer) === "terrain"
          ? "terrain"
          : catalogLayer.kind
        : metadataLayerKind;
      onSelect(
        selectByIndexNodeId(nodeId, {
          source: "scene-tree",
          layerId: node?.layerId ?? undefined,
          layerKind,
          sceneItemId: node?.layerId ?? undefined,
          featureId:
            node?.featureId !== null && node?.featureId !== undefined
              ? node.featureId
              : undefined,
          nodeKind: node?.kind,
          detailsPanel: "layer-properties",
        }),
      );
    },
    [nodeById, onSelect],
  );

  // Isolate single node — or isolate the entire block when the target is a block-group.
  const handleIsolate = useCallback(
    (nodeId: string) => {
      const node = nodeById.get(nodeId);
      if (node && (node.metadata as Record<string, unknown> | undefined)?.isBlockGroup) {
        isolateBlock(nodeId);
      } else {
        isolate(nodeId, "spatial");
      }
    },
    [nodeById],
  );

  // V4 — Provenance panel: when a layer-group node is selected, derive the
  // provenance URL from the hierarchy URL (sibling file convention).
  const [showProvenance, setShowProvenance] = useState(false);
  const selectedLayerProvenance = useMemo(() => {
    if (!selectedNode) return null;
    const meta = selectedNode.metadata as Record<string, unknown> | undefined;
    if (!meta?.isLayerGroup) return null;
    const layerId = selectedNode.layerId;
    if (!layerId) return null;
    const ref = sceneHierarchies.find((h) => h.layerId === layerId);
    if (!ref) return null;
    // Convention: provenance.json is at the same directory level as the hierarchy file.
    const lastSlash = ref.hierarchyUrl.lastIndexOf("/");
    const baseDir = lastSlash >= 0 ? ref.hierarchyUrl.slice(0, lastSlash) : "";
    // V-CRS-B — prefer an explicit crsProvenanceUrl, else the sibling provenance.json.
    return {
      url: ref.crsProvenanceUrl ?? `${baseDir}/provenance.json`,
      name: ref.displayName,
      sourceCrs: ref.sourceCrs,
      displayCrs: ref.displayCrs,
      verticalDatum: ref.verticalDatum,
    };
  }, [selectedNode, sceneHierarchies]);

  // Auto-show provenance when a layer-group is first selected
  useEffect(() => {
    if (selectedLayerProvenance) setShowProvenance(true);
    else setShowProvenance(false);
  }, [selectedLayerProvenance]);

  // Hoisted out of JSX (and above the early return below) so these hooks are
  // called unconditionally in a stable order every render (react-hooks/rules-of-hooks).
  const handleToggleVisibility = useCallback((nodeKey: string) => {
    const store = useSceneHierarchyStore.getState();
    if (!manifest) return;
    const node = manifest.nodes.find((n) => n.id === nodeKey || n.objectId === nodeKey);
    if (!node) return;
    const objectId = node.objectId;
    const layerId = (node as { layerId?: string }).layerId;
    const catalogLayerNode =
      Boolean(layerId) && isCatalogLayerVisibilityNode(node);

    const childLayerIds = new Set<string>();
    if (!catalogLayerNode) {
      const visit = (parentId: string) => {
        for (const child of manifest.nodes) {
          if (child.parentId !== parentId) continue;
          if (child.layerId && isCatalogLayerVisibilityNode(child)) {
            childLayerIds.add(child.layerId);
          }
          visit(child.id);
        }
      };
      visit(node.id);
    }

    if (childLayerIds.size > 0) {
      const visibleLayers = useViewerStore.getState().visibleLayerIds;
      const nextVisible = [...childLayerIds].some((id) => !visibleLayers.has(id));
      for (const childLayerId of childLayerIds) {
        onLayerVisibilityChange?.(childLayerId, nextVisible);
      }
      store.setControlOverlayEntry(objectId, { visible: nextVisible });
      return;
    }

    const wasVisible = catalogLayerNode
      ? useViewerStore.getState().visibleLayerIds.has(layerId!)
      : store.controlOverlay.byObject[objectId]?.visible !== false;
    const nextVisible = !wasVisible;
    if (!catalogLayerNode) {
      toggleVisibility(objectId);
    }
    const md = node.metadata as Record<string, unknown> | undefined;
    const blockCode = md?.blockCode as string | undefined;
    const isBlockGroup = md?.isBlockGroup === true;
    if (blockCode && isBlockGroup) {
      if (layerId) onBlockVisibilityChange?.(layerId, blockCode, nextVisible);
      return;
    }
    // Plain layer node (COG terrain / raster / tileset root): toggle the
    // whole rendered layer in the engine.
    if (layerId) onLayerVisibilityChange?.(layerId, nextVisible);
  }, [onBlockVisibilityChange, onLayerVisibilityChange, manifest]);

  const handleOpacityChange = useCallback((objectId: string, value: number) => {
    setOpacity(objectId, value);
    if (!manifest) return;
    const node = manifest.nodes.find((n) => n.objectId === objectId);
    if (!node) return;
    const md = node.metadata as Record<string, unknown> | undefined;
    const blockCode = md?.blockCode as string | undefined;
    const isBlockGroup = md?.isBlockGroup === true;
    const layerId = (node as { layerId?: string }).layerId;
    if (blockCode && isBlockGroup) {
      if (layerId) onBlockOpacityChange?.(layerId, blockCode, value);
      return;
    }
    // Plain layer node (COG terrain / raster / tileset root).
    if (layerId) onLayerOpacityChange?.(layerId, value);
  }, [onBlockOpacityChange, onLayerOpacityChange, manifest]);

  // An objectIndex project (inline, fetched-by-URL, or multi-layer) is inherently
  // V2 — render regardless of the flag. Legacy projects still require the flag.
  const shouldRender =
    featureFlagEnabled || !!objectIndex || !!urlObjectIndexManifest || layerManifests.size > 0;
  if (!shouldRender) {
    return null;
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      <div style={{ flex: 1, minHeight: 0 }}>
        <SceneTreeV2
          manifest={manifest}
          selectedNodeId={selectedNodeId}
          onSelect={handleSelect}
          onLazyLoad={handleLazyLoad}
          overlayByObject={overlayByObject}
          visibleLayerIds={visibleLayerIds}
          onToggleVisibility={handleToggleVisibility}
          onIsolateSystem={(nodeId) => isolateSystem(nodeId, "functional")}
          onColorByAspect={(aspect) => colorByAspect(aspect)}
          onClearOverlay={clearIsolation}
          onOpacityChange={handleOpacityChange}
          onZoomTo={handleZoomTo}
          onOpenSettings={handleOpenSettings}
          onIsolate={handleIsolate}
          onShowAll={handleShowAll}
          onCollapse={handleCollapse}
        />
      </div>

      {/* Selected-node quick controls — a FIXED strip (never floats over tree
          rows). Drives per-object overlay opacity, and for layer nodes the
          rendered layer opacity, via the same handler the rows used. */}
      {selectedNode ? (
        <SelectionOpacityStrip
          node={selectedNode}
          opacity={overlayByObject?.[selectedNode.objectId]?.opacity ?? 1}
          onOpacityChange={handleOpacityChange}
        />
      ) : null}

      {/* V4 — Layer provenance panel (when a layer-group is selected) */}
      {showProvenance && selectedLayerProvenance && (
        <div
          style={{
            flexShrink: 0,
            borderTop: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
            maxHeight: 200,
            overflowY: "auto",
          }}
        >
          <LayerProvenancePanel
            provenanceUrl={selectedLayerProvenance.url}
            layerName={selectedLayerProvenance.name}
            sourceCrs={selectedLayerProvenance.sourceCrs}
            displayCrs={selectedLayerProvenance.displayCrs}
            verticalDatum={selectedLayerProvenance.verticalDatum}
            onClose={() => setShowProvenance(false)}
          />
        </div>
      )}

      {/* Block 2, Task B — selected-node detail strip with relationship chips */}
      {selectedNode && (selectedNode.relationships?.length ?? 0) > 0 && (
        <div
          data-testid="scene-tree-detail-strip"
          style={{
            flexShrink: 0,
            borderTop: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
            backgroundColor: tokens.color.surface[1],
            maxHeight: 160,
            overflowY: "auto",
          }}
        >
          <RelationshipChips
            node={selectedNode}
            nodeById={nodeById}
            onSelectTarget={handleNavigateToTarget}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Fixed opacity strip for the selected node. Lives at the bottom of the Scene
 * Tree (not as a floating popover over rows), so it has a stable, predictable
 * place. Calls `onOpacityChange(objectId, value)` — identical wiring to the old
 * per-row slider, so per-object overlay opacity and layer opacity still update.
 */
function SelectionOpacityStrip({
  node,
  opacity,
  onOpacityChange,
}: {
  readonly node: { objectId: string; label: string };
  readonly opacity: number;
  readonly onOpacityChange: (objectId: string, value: number) => void;
}) {
  const clamped = Math.max(0, Math.min(1, opacity));
  const shortLabel = node.label.includes(".")
    ? node.label.slice(node.label.lastIndexOf(".") + 1)
    : node.label;
  return (
    <div
      data-testid="scene-tree-selection-controls"
      style={{
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: tokens.space[2],
        padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
        borderTop: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
        backgroundColor: tokens.color.surface[1],
        minHeight: tokens.density.rowDense,
      }}
    >
      <span
        title={node.label}
        style={{
          fontSize: tokens.font.size.xs,
          fontWeight: tokens.font.weight.medium,
          color: tokens.color.ink.secondary,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: tokens.space[8] + tokens.space[5],
        }}
      >
        {shortLabel}
      </span>
      <span
        style={{
          fontSize: tokens.font.size.xs,
          color: tokens.color.ink.muted,
          whiteSpace: "nowrap",
        }}
      >
        Opacity
      </span>
      <input
        type="range"
        data-testid="selection-opacity-slider"
        aria-label={`Opacity for ${node.label}`}
        min={0}
        max={1}
        step={0.05}
        value={clamped}
        onChange={(e) => onOpacityChange(node.objectId, parseFloat(e.target.value))}
        style={{
          flex: 1,
          minWidth: 0,
          accentColor: tokens.color.accent.primary,
          cursor: "pointer",
        }}
      />
      <span
        style={{
          fontSize: tokens.font.size.xs,
          fontFamily: tokens.font.family.mono ?? "monospace",
          color: tokens.color.ink.secondary,
          minWidth: tokens.space[7],
          textAlign: "right",
        }}
      >
        {Math.round(clamped * 100)}%
      </span>
    </div>
  );
}
